import { useEffect, useMemo, useRef, useState } from "react";
import {
  VStack,
  HStack,
  Divider,
  Text,
  Icon,
  Button,
  chakra,
  Box,
} from "@chakra-ui/react";
import { Controller, useFormContext, useFieldArray } from "react-hook-form";
import Section, { Header } from "../../Components/Section";
import AddBlockToggle from "../../Components/AddBlockToggle";
import { LayoutGroup, motion, Reorder, useDragControls } from "framer-motion";
import { nanoid } from "nanoid";
import KYDTextArea from "src/ui/KYDTextArea";
import KYDInput from "src/ui/KYDInput";
import KYDCheckbox from "src/ui/KYDCheckbox";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import EventBlock from "../../Components/EventBlock";
import KYDButton from "src/ui/KYDButton";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { FiPlus, FiSend } from "react-icons/fi";
import {
  BlockType,
  KYDEventBlockDataV2,
  EventListData,
  ImageData,
  Image2ColData,
  TextBodyData,
  TextBodyDataV2,
  VideoData,
  BodyDoc,
} from "@common/interfaces/KYDCampaign";
import {
  KYDAdminFeed,
  KYDOrganizationFull,
} from "@common/interfaces/KYDOrganization";
import KYDDateTimePicker from "src/ui/KYDDateTimePicker";
import { KYDImageUpload } from "src/ui";
import KYDMultiSelect from "src/ui/KYDMultiSelect";
import KYDVideoUpload from "src/KYDVideoUpload";
import EventListItem from "../../Components/EventListItem";
import { FormValues } from ".";
import { AlgoliaSegmentObject } from "@common/interfaces/Algolia";
import {
  Configure,
  InstantSearch,
  useInfiniteHits,
  useSearchBox,
} from "react-instantsearch";
import {
  DEFAULT_EVENT_BLOCK_DATA,
  DEFAULT_EVENT_LIST_ITEM_DATA,
} from "common/email-templates/dynamic-template-blocks";
import dayjs from "dayjs";
import HeadlineBlock from "../../Components/HeadlineBlock";
import DraggableItem from "./DraggableItem";
import { S3Response } from "react-s3-uploader";
import { KYDScope } from "@common/interfaces/ICognito";
import ShowIfHasScope from "src/Manager/ShowIfHasScope";
import { TipTapEditor } from "src/ui/editor";
import { Editor } from "@tiptap/react";

const Group = chakra(Reorder.Group);

// Utility function to format relative time
function formatRelativeTime(date: Date | string | number): string {
  const now = new Date();
  const input = new Date(date);
  const diff = Math.floor((now.getTime() - input.getTime()) / 1000); // in seconds

  if (isNaN(diff)) return "just now";
  if (diff < 5) return "just now";
  if (diff < 60) return `${diff} seconds ago`;
  if (diff < 3600) {
    const mins = Math.floor(diff / 60);
    return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  }
  if (diff < 86400) {
    const hours = Math.floor(diff / 3600);
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }
  if (diff < 172800) {
    return "yesterday";
  }
  const days = Math.floor(diff / 86400);
  return `${days} days ago`;
}

const EVENTS_FILTER_QUERY = "status:OPEN OR status:PENDING_ANNOUNCEMENT";

const BuildCampaignForm = ({
  permissions,
  isMobile,
  organization,
  externalEvents,
  searchClient,
  indexName,
  handleNext,
  createUploadUrl,
  loading = {
    savingCampaign: false,
  },
}: {
  permissions: KYDScope[];
  isMobile: boolean;
  organization: KYDOrganizationFull;
  externalEvents: KYDAdminFeed["external_events"];
  searchClient: any;
  indexName: string;
  handleNext: () => void;
  createUploadUrl: ({
    file,
    callback,
  }: {
    file: File;
    callback: (params: S3Response) => void | Promise<void | string>;
  }) => Promise<{ signedUrl: string; fileKey: string }>;
  loading?: {
    savingCampaign: boolean;
  };
}) => {
  const form = useFormContext<FormValues>();
  const { control, watch, trigger, setValue, getValues } = form;
  const containerRef = useRef<HTMLDivElement>(null);

  const { items: segments } = useInfiniteHits<AlgoliaSegmentObject>({
    escapeHTML: false,
    showPrevious: false,
  });

  const { refine } = useSearchBox();

  const canEdit = useMemo(() => {
    return (
      permissions.includes("crm/campaigns:write") ||
      permissions.includes("crm/write")
    );
  }, [permissions]);

  const {
    append,
    move,
    remove,
    update,
    fields: blocks,
  } = useFieldArray<FormValues, "blocks", "itemId">({
    control,
    name: "blocks",
    keyName: "itemId",
  });

  const navigate = useNavigate();
  const isFormDisabled = loading.savingCampaign || !canEdit;
  const [localOrder, setLocalOrder] = useState<string[]>([]);
  const isReordering = useRef(false);

  useEffect(() => {
    if (blocks?.length) {
      // Get current block IDs
      const currentBlockIds = blocks.map((b) => b.id);

      // If localOrder is empty, initialize it
      if (!localOrder.length) {
        setLocalOrder(currentBlockIds);
      } else {
        // Check if there are any new blocks that aren't in localOrder
        const newBlockIds = currentBlockIds.filter(
          (id) => !localOrder.includes(id)
        );
        if (newBlockIds.length > 0) {
          // Add new blocks to the end of localOrder
          setLocalOrder([...localOrder, ...newBlockIds]);
        }
      }
    }
  }, [blocks, localOrder]);

  const handleReorder = (newOrder: string[]) => {
    if (!isReordering.current) {
      isReordering.current = true;
      setLocalOrder(newOrder);
      const blocksInNewOrder = newOrder
        .map((id) => blocks.find((b) => b.id === id))
        .filter(Boolean);
      if (blocksInNewOrder.length === blocks.length) {
        setValue("blocks", blocksInNewOrder, { shouldDirty: true });
      }

      // Reset the flag after a short delay to allow for animation
      setTimeout(() => {
        isReordering.current = false;
      }, 100);
    }
  };

  const substitutionsItems = [
    { key: "{{event_name}}", label: "Event Name", value: "{{event_name}}" },
    { key: "{{start_at}}", label: "Start At", value: "{{start_at}}" },
    { key: "{{end_at}}", label: "End At", value: "{{end_at}}" },
    { key: "{{venue}}", label: "Venue", value: "{{venue}}" },
  ];

  const handleAddBlock = (type: BlockType) => {
    const newId = nanoid();
    switch (type) {
      case "textHeadline":
        append(
          {
            id: newId,
            type,
            preheadline: "prehead",
            headline: "Headline",
            subheadline: "subheadline",
            link_headline_to_event: false,
          },
          { shouldFocus: true }
        );
        break;
      case "textBody":
        append(
          templateVersion === 4
            ? {
                id: newId,
                type: "textBody",
                body: {
                  type: "doc",
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Event Copy",
                        },
                      ],
                    },
                  ],
                },
              }
            : { id: newId, type: "textBody", body: "Event Copy" },
          { shouldFocus: true }
        );
        break;
      case "event1col":
        append(
          {
            id: newId,
            type,
            event: DEFAULT_EVENT_BLOCK_DATA,
          },
          { shouldFocus: true }
        );
        break;
      case "event2col":
        append(
          {
            id: newId,
            type,
            events: [DEFAULT_EVENT_BLOCK_DATA, DEFAULT_EVENT_BLOCK_DATA],
          },
          {
            shouldFocus: true,
          }
        );
        break;
      case "eventList":
        append(
          {
            id: newId,
            type,
            list_heading: "List heading",
            events: [JSON.parse(JSON.stringify(DEFAULT_EVENT_LIST_ITEM_DATA))],
          },
          { shouldFocus: true }
        );
        break;
      case "image":
        append({ id: newId, type, src: "" }, { shouldFocus: true });
        break;
      case "image2col":
        append(
          {
            id: newId,
            type,
            images: [
              { id: nanoid(), type: "image", src: "" },
              { id: nanoid(), type: "image", src: "" },
            ],
          },
          { shouldFocus: true }
        );
        break;
      case "video":
        append(
          { id: newId, type, src: "", preview_src: "" },
          { shouldFocus: true }
        );
        break;
    }

    // Scroll to the new block after it's added
    setTimeout(() => {
      const newBlock = document.getElementById(newId);
      if (newBlock && containerRef.current) {
        newBlock.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  };

  const handleRemoveBlock = (index: number) => {
    remove(index);
  };

  const isSendImmediately = watch("send_at") ? false : true;
  const templateVersion = watch("template_version");

  return (
    <LayoutGroup>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <VStack
          p={8}
          pt={0}
          w={isMobile ? "100%" : "443px"}
          flexShrink={0}
          bg="white"
          spacing={0}
          h="100%"
          alignItems="flex-start"
          position="relative"
          overflowY="auto"
          ref={containerRef}
          height="100%"
          pb={8}
        >
          <HStack
            gap={6}
            w="100%"
            justifyContent="space-between"
            position="sticky"
            top={0}
            zIndex={100}
            bg="white"
            py={4}
          >
            <KYDButton
              variant="outline"
              leftIcon={<Icon as={ChevronLeftIcon} fontSize="xl" />}
              onClick={() => navigate(-1)}
              pr={3}
              px={2}
            >
              Back
            </KYDButton>
            {watch("updated_at") && (
              <Text fontSize="xs" textAlign="center">
                Saved {formatRelativeTime(watch("updated_at"))}
              </Text>
            )}
            <ShowIfHasScope
              permissions={permissions}
              checkFor={["crm/campaigns:write", "crm/write"]}
            >
              <KYDButton
                onClick={handleNext}
                backgroundColor="black"
                type="submit"
                rightIcon={<Icon as={ChevronRightIcon} fontSize="xl" />}
                pl={3}
                px={2}
                isLoading={loading.savingCampaign}
                isDisabled={
                  !watch("name") ||
                  !watch("subject") ||
                  !watch("segments")?.length ||
                  isFormDisabled
                }
              >
                Next
              </KYDButton>
            </ShowIfHasScope>
          </HStack>

          <HStack w="100%" alignItems="flex-start" mt={6}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <KYDInput
                  {...field}
                  label="Campaign Name"
                  placeholder="Enter title"
                />
              )}
            />
          </HStack>

          <VStack w="100%" mt={6}>
            <Header title="Time To Send" />
            <HStack gap={4} w="100%">
              <KYDCheckbox
                id="send-immediately"
                isChecked={isSendImmediately}
                onChange={(e) => {
                  setValue("send_at", null, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
                label="Send immediately"
                isDisabled={isFormDisabled}
              />
              <KYDCheckbox
                id="schedule-send"
                isChecked={!isSendImmediately}
                onChange={(e) =>
                  setValue(
                    "send_at",
                    e.target.checked
                      ? dayjs()
                          .tz(organization?.settings?.timezone)
                          .add(1, "hour")
                          .toISOString()
                      : null,
                    {
                      shouldDirty: true,
                      shouldTouch: true,
                    }
                  )
                }
                label="Schedule Send"
                isDisabled={isFormDisabled}
              />
            </HStack>
          </VStack>
          {!isSendImmediately && (
            <HStack gap={4} w="100%" mt={6}>
              <KYDDateTimePicker
                label="Send Date"
                name="send_at"
                timezone={organization?.settings?.timezone}
                value={watch("send_at")}
                onChange={(date) => {
                  setValue("send_at", date, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
                isDisabled={isFormDisabled}
              />
            </HStack>
          )}
          <VStack w="100%" mt={6}>
            <Header title="Audience" />
            <KYDMultiSelect
              placeholder="Search for a segment"
              values={Array.from(
                new Map(
                  (form.watch("segments") || []).map((segment) => [
                    segment.id,
                    {
                      label: `${segment.name}`,
                      value: segment.id,
                    },
                  ])
                ).values()
              )}
              label="Segments"
              onChange={(values) => {
                const selected = values || [];
                const uniqueSelected = Array.from(
                  new Map(
                    selected.map((value) => [value.value, value])
                  ).values()
                );
                const segmentOptionsById = new Map(
                  segments.map((segment) => [
                    segment.objectID,
                    {
                      name: segment.name,
                      size: segment.size,
                    },
                  ])
                );
                setValue(
                  "segments",
                  uniqueSelected.map((value) => ({
                    name:
                      value.label ||
                      segmentOptionsById.get(value.value)?.name ||
                      "",
                    id: value.value,
                    size: segmentOptionsById.get(value.value)?.size || 0,
                  }))
                );
              }}
              onInputChange={refine}
              maxAllowed={10}
              options={segments.map((segment) => ({
                label: `${segment.name}`,
                value: segment.objectID,
              }))}
              isDisabled={isFormDisabled}
            />
          </VStack>
          <Section
            title="Subject"
            isDragable={false}
            isDisabled={isFormDisabled}
          >
            <Controller
              name="subject"
              control={control}
              render={({ field }) => (
                <KYDTextArea
                  {...field}
                  placeholder="Enter subject line"
                  isDisabled={isFormDisabled}
                />
              )}
            />
            <Controller
              name="preheader"
              control={control}
              render={({ field }) => (
                <KYDInput
                  {...field}
                  label="Preheader text"
                  placeholder="for email"
                  isDisabled={isFormDisabled}
                />
              )}
            />
          </Section>

          <Group
            axis="y"
            onReorder={handleReorder}
            values={localOrder}
            listStyleType={"none"}
            display="flex"
            width="100%"
            flexDirection="column"
            gap={4}
            draggable={false}
          >
            {localOrder
              .map((id) => blocks.find((b) => b.id === id))
              .filter(Boolean)
              .map((blk, index) => (
                <DraggableItem
                  key={`${blk?.id}`}
                  id={blk?.id}
                  isDisabled={isFormDisabled}
                >
                  {(() => {
                    if (!blk.type) {
                      return (
                        <Section
                          title="Invalid block"
                          onRemove={() => handleRemoveBlock(index)}
                          isDisabled={isFormDisabled}
                          // onPointerDown will be injected by DraggableItem
                        >
                          <Text>Invalid block</Text>
                        </Section>
                      );
                    }

                    switch (blk.type) {
                      case "textHeadline":
                        return (
                          <Section
                            title="Headline"
                            onRemove={() => handleRemoveBlock(index)}
                            isDisabled={isFormDisabled}
                            // onPointerDown will be injected by DraggableItem
                          >
                            <InstantSearch
                              indexName={indexName}
                              searchClient={searchClient}
                            >
                              <Configure filters={EVENTS_FILTER_QUERY} />
                              <HeadlineBlock
                                index={index}
                                control={control}
                                setValue={(value) => {
                                  update(index, value);
                                  trigger(`blocks.${index}`);
                                }}
                                isDisabled={isFormDisabled}
                              />
                            </InstantSearch>
                          </Section>
                        );
                      case "textBody":
                        return (
                          <Section
                            title="Body Text"
                            onRemove={() => handleRemoveBlock(index)}
                            isDisabled={isFormDisabled}
                            p={templateVersion >= 4 ? 0 : undefined}
                            // onPointerDown will be injected by DraggableItem
                          >
                            {templateVersion >= 4 ? (
                              <Box
                                minH={"400px"}
                                w="full"
                                borderWidth={1}
                                borderColor={"gray.300"}
                                borderRadius={"md"}
                              >
                                <TipTapEditor
                                  value={blk.body as BodyDoc}
                                  isDisabled={isFormDisabled}
                                  onChange={(value) => {
                                    update(index, {
                                      ...(blocks[index] as TextBodyDataV2),
                                      body: value,
                                    } as TextBodyDataV2);
                                  }}
                                />
                              </Box>
                            ) : (
                              <KYDTextArea
                                value={blk.body as string}
                                isDisabled={isFormDisabled}
                                label="Body Text"
                                onChange={(value) => {
                                  update(index, {
                                    ...(blocks[index] as TextBodyData),
                                    body: value.target.value,
                                  } as TextBodyData);
                                }}
                              />
                            )}
                          </Section>
                        );
                      case "event1col":
                        return (
                          <Section
                            title="Event (1-col)"
                            onRemove={() => handleRemoveBlock(index)}
                            isDisabled={isFormDisabled}
                            // onPointerDown will be injected by DraggableItem
                          >
                            <InstantSearch
                              indexName={indexName}
                              searchClient={searchClient}
                            >
                              <Configure filters={EVENTS_FILTER_QUERY} />
                              <EventBlock
                                createUploadUrl={createUploadUrl}
                                externalEvents={externalEvents}
                                type="event1col"
                                timezone={organization.settings.timezone}
                                blockIndex={index}
                                eventIndex={0}
                                substitutionsItems={substitutionsItems}
                                eventData={blk.event as KYDEventBlockDataV2}
                                isDisabled={isFormDisabled}
                              />
                            </InstantSearch>
                          </Section>
                        );
                      case "event2col":
                        return (
                          <Section
                            title="Event (2-col)"
                            onRemove={() => handleRemoveBlock(index)}
                            isDisabled={isFormDisabled}
                            // onPointerDown will be injected by DraggableItem
                          >
                            <InstantSearch
                              indexName={indexName}
                              searchClient={searchClient}
                            >
                              <Configure filters={EVENTS_FILTER_QUERY} />
                              <EventBlock
                                createUploadUrl={createUploadUrl}
                                externalEvents={externalEvents}
                                type="event2col"
                                timezone={organization.settings.timezone}
                                blockIndex={index}
                                eventIndex={0}
                                substitutionsItems={substitutionsItems}
                                eventData={blk.events[0]}
                                isDisabled={isFormDisabled}
                              />
                              <Divider borderColor="#00000033" />
                              <EventBlock
                                createUploadUrl={createUploadUrl}
                                externalEvents={externalEvents}
                                type="event2col"
                                timezone={organization.settings.timezone}
                                blockIndex={index}
                                eventIndex={1}
                                substitutionsItems={substitutionsItems}
                                eventData={blk.events[1]}
                                isDisabled={isFormDisabled}
                              />
                            </InstantSearch>
                          </Section>
                        );
                      case "eventList":
                        return (
                          <Section
                            title="Event List"
                            onRemove={() => handleRemoveBlock(index)}
                            isDisabled={isFormDisabled}
                            // onPointerDown will be injected by DraggableItem
                          >
                            <Controller
                              name={`blocks.${index}.list_heading` as const}
                              control={control}
                              render={({ field }) => (
                                <KYDInput
                                  {...field}
                                  label="List Heading"
                                  placeholder="Optional"
                                  isDisabled={isFormDisabled}
                                />
                              )}
                            />

                            <Divider borderColor="#00000033" mt={4} mb={4} />

                            {blk.events.map((event, eventIndex) => (
                              <InstantSearch
                                indexName={indexName}
                                key={`${event.id}-${eventIndex}`}
                                searchClient={searchClient}
                              >
                                <Configure filters={EVENTS_FILTER_QUERY} />
                                <Text fontSize="small" color="gray.500">
                                  Event {eventIndex + 1}
                                </Text>
                                <EventListItem
                                  eventData={event}
                                  blockIndex={index}
                                  eventIndex={eventIndex}
                                  timezone={organization.settings.timezone}
                                  isDisabled={isFormDisabled}
                                />
                                <Text
                                  fontSize="small"
                                  color="blue.500"
                                  cursor="pointer"
                                  onClick={() => {
                                    update(index, {
                                      ...(blocks[index] as EventListData),
                                      events: blk.events.filter(
                                        (e) => e.id !== event.id
                                      ),
                                    });
                                  }}
                                >
                                  Remove event
                                </Text>
                                <Divider
                                  borderColor="#00000033"
                                  mt={2}
                                  mb={2}
                                />
                              </InstantSearch>
                            ))}

                            <Button
                              variant="link"
                              bg="white"
                              color="black"
                              py={"12px"}
                              px={"10px"}
                              fontWeight="normal"
                              border="1px solid #E6E6E6"
                              borderRadius="40px"
                              boxShadow="0px 10px 15px 0px rgba(0, 0, 0, 0.05)"
                              fontSize="13px"
                              leftIcon={<FiPlus />}
                              isDisabled={isFormDisabled}
                              onClick={() => {
                                const new_blk = form.getValues(
                                  `blocks.${index}`
                                ) as EventListData;

                                update(index, {
                                  ...new_blk,
                                  events: [
                                    ...new_blk.events,
                                    {
                                      ...DEFAULT_EVENT_LIST_ITEM_DATA,
                                      id: nanoid(),
                                    },
                                  ],
                                });
                              }}
                            >
                              Add Event
                            </Button>
                          </Section>
                        );
                      case "image":
                        return (
                          <Section
                            title="Image"
                            onRemove={() => handleRemoveBlock(index)}
                            isDisabled={isFormDisabled}
                            // onPointerDown will be injected by DraggableItem
                          >
                            <KYDImageUpload
                              image={(blocks[index] as ImageData)?.src}
                              link={(blocks[index] as ImageData)?.link || ""}
                              createUploadUrl={createUploadUrl}
                              supportLink
                              onChange={(src) => {
                                update(index, {
                                  ...(blocks[index] as ImageData),
                                  src: src.cdn_url,
                                  link: src.link,
                                } as ImageData);
                              }}
                              isDisabled={isFormDisabled}
                            />
                          </Section>
                        );
                      case "image2col":
                        return (
                          <Section
                            title="Image (2-col)"
                            onRemove={() => handleRemoveBlock(index)}
                            isDisabled={isFormDisabled}
                            // onPointerDown will be injected by DraggableItem
                          >
                            <HStack
                              w="100%"
                              alignItems="flex-start"
                              spacing={4}
                              flexDirection="column"
                            >
                              {(blocks[index] as Image2ColData).images.map(
                                (image, imageIndex) => (
                                  <KYDImageUpload
                                    key={image.id || `${index}-${imageIndex}`}
                                    image={image.src}
                                    link={image.link || ""}
                                    createUploadUrl={createUploadUrl}
                                    supportLink
                                    onChange={(src) => {
                                      const existing = blocks[
                                        index
                                      ] as Image2ColData;
                                      const nextImages = [
                                        ...existing.images,
                                      ] as [ImageData, ImageData];
                                      nextImages[imageIndex] = {
                                        ...nextImages[imageIndex],
                                        src: src.cdn_url,
                                        link: src.link,
                                      };
                                      update(index, {
                                        ...existing,
                                        images: nextImages,
                                      } as Image2ColData);
                                    }}
                                    isDisabled={isFormDisabled}
                                  />
                                )
                              )}
                            </HStack>
                          </Section>
                        );
                      case "video":
                        return (
                          <Section
                            title="Video"
                            onRemove={() => handleRemoveBlock(index)}
                            isDisabled={isFormDisabled}
                            // onPointerDown will be injected by DraggableItem
                          >
                            <KYDVideoUpload
                              createUploadUrl={createUploadUrl}
                              url={(blocks[index] as VideoData)?.src}
                              preview_src={
                                (blocks[index] as VideoData)?.preview_src
                              }
                              onChange={(src) => {
                                update(index, {
                                  ...(blocks[index] as VideoData),
                                  ...src,
                                } as VideoData);
                              }}
                              isDisabled={isFormDisabled}
                            />
                          </Section>
                        );
                      default:
                        return null;
                    }
                  })()}
                </DraggableItem>
              ))}
          </Group>

          <AddBlockToggle
            isDisabled={isFormDisabled}
            onAddBlock={handleAddBlock}
          />
        </VStack>
      </LocalizationProvider>
    </LayoutGroup>
  );
};

export default BuildCampaignForm;
